import React, { useEffect, useRef, useState } from 'react';

import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  type Label,
  type LabelMatingInfo,
  useCreateEmptyLabelMutation,
  useGenerateLabelsMutation,
  useGetLabelsQuery,
  useLazyValidateLabelMoveQuery,
  useMoveDetailBetweenLabelsMutation,
  useReorderLabelsMutation,
} from '@/app/services/fertWork';
import { addToast } from '@/app/slices/toast';
import { PageStatusBanner } from '@/components/common';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useFertWorkLabelsReport,
  useFertWorkSingleLabelReport,
} from '@/services/reports';
import { EMBRYO_CATEGORY_COLOR_KEY_ITEMS } from '@/utils/categoryColors';

import { LabelCard } from '../LabelCard';
import { useRegenerateConfirm } from '../RegenerateConfirm';
import { SortableLabel } from './SortableLabel';

export type GenerateLabelsProps = {
  sessionId: string;
  className?: string;
  isCommercial?: boolean;
};

export const GenerateLabels = ({
  sessionId,
  className = '',
  isCommercial = false,
}: GenerateLabelsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const showRegenerateConfirm = useRegenerateConfirm();

  const [activeLabel, setActiveLabel] = useState<Label | null>(null);
  const [localLabels, setLocalLabels] = useState<Label[]>([]);
  const [showFab, setShowFab] = useState(false);
  const [selectedLabelId, setSelectedLabelId] = useState<string | null>(null);
  const actionsRowRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
  );

  const {
    data: labels = [],
    isLoading,
    error,
    refetch,
  } = useGetLabelsQuery(sessionId || '', {
    skip: !sessionId,
  });
  const [generateLabels, { isLoading: isGenerating }] =
    useGenerateLabelsMutation();
  const [reorderLabels, { isLoading: isReordering }] =
    useReorderLabelsMutation();
  const [createEmptyLabel, { isLoading: isCreatingLabel }] =
    useCreateEmptyLabelMutation();
  const [moveDetail] = useMoveDetailBetweenLabelsMutation();
  const [validateLabelMove] = useLazyValidateLabelMoveQuery();
  const [generateLabelsPdf, { isPending: isDownloadingAll }] =
    useFertWorkLabelsReport();
  const [generateSingleLabelPdf] = useFertWorkSingleLabelReport();
  const [draggedMating, setDraggedMating] = useState<{
    detailId: string;
    position: string;
    label: Label;
  } | null>(null);
  const isBusy = isGenerating || isReordering;

  const collisionDetection: CollisionDetection = (args) => {
    const pointerCollisions = pointerWithin(args);
    const dropzoneHits = pointerCollisions.filter((c) =>
      String(c.id).startsWith('dropzone:'),
    );
    if (dropzoneHits.length > 0) return dropzoneHits;
    if (pointerCollisions.length > 0) return pointerCollisions;
    return rectIntersection(args);
  };

  useEffect(() => {
    if (labels.length > 0) {
      setLocalLabels([...labels]);
    }
  }, [labels]);

  useEffect(() => {
    const el = actionsRowRef.current;
    if (!el) {
      setShowFab(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setShowFab(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [labels.length > 0]);

  const persistCurrentLabelOrder = async () => {
    if (!sessionId || localLabels.length === 0) return;

    const newOrder = localLabels.map((label, index) => ({
      labelId: label.id,
      newDisplayOrder: index + 1,
    }));

    await reorderLabels({ sessionId, newOrder }).unwrap();
  };

  const sideKey = (m?: LabelMatingInfo) =>
    m
      ? [
          m.detailId,
          m.donorName,
          m.sireName,
          m.semenType,
          m.shipLabel,
          m.oocytes,
          m.priority ?? '',
        ].join('|')
      : '';

  const summarizeRetainedChanges = (before: Label[], after: Label[]) => {
    const beforeById = new Map(before.map((l) => [l.id, l]));
    const afterIds = new Set(after.map((l) => l.id));
    const added = after.filter((l) => !beforeById.has(l.id)).length;
    const removed = before.filter((l) => !afterIds.has(l.id)).length;
    const updated = after.filter((l) => {
      const prev = beforeById.get(l.id);
      return (
        !!prev &&
        (sideKey(prev.top) !== sideKey(l.top) ||
          sideKey(prev.bottom) !== sideKey(l.bottom))
      );
    }).length;
    return { added, removed, updated };
  };

  const handleGenerateLabels = async (retainExisting = false) => {
    if (!sessionId) return;

    try {
      if (retainExisting) {
        await persistCurrentLabelOrder();
      }

      const before = retainExisting ? [...labels] : [];
      const after = await generateLabels({
        sessionId,
        retainExisting,
      }).unwrap();

      let detail = t(
        'fertWork.toasts.labelsGenerated',
        'Labels generated successfully',
      );
      if (retainExisting) {
        const { updated, added, removed } = summarizeRetainedChanges(
          before,
          after,
        );
        detail =
          updated + added + removed === 0
            ? t(
                'fertWork.toasts.labelsRetainedNoChanges',
                'Layout retained — no data changes found',
              )
            : t('fertWork.toasts.labelsRetained', {
                defaultValue:
                  'Layout retained — {updated} plate(s) updated, {added} added, {removed} removed',
                updated,
                added,
                removed,
              });
      }

      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.success', 'Success'),
          detail,
        }),
      );
      refetch();
    } catch (err: any) {
      console.error('Failed to generate labels:', err);
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error', 'Error'),
          detail:
            err?.data ||
            t(
              'fertWork.toasts.failedToGenerateLabels',
              'Failed to generate labels',
            ),
        }),
      );
    }
  };

  const handleRegenerateLabels = () => {
    showRegenerateConfirm({
      header: t('fertWork.regenerateLabels', 'Regenerate Labels'),
      description: t(
        'fertWork.regenerateLabelsDescription',
        'Choose whether to reset label arrangement or retain current order and manual changes while regenerating.',
      ),
      warningTitle: t('fertWork.regenerateOptions', 'Regenerate options'),
      warningDetail: t(
        'fertWork.regenerateKeepOrResetLabels',
        'Reset will rebuild labels from scratch. Retain will regenerate but keep existing label order and manual mating moves when possible.',
      ),
      acceptLabel: t('fertWork.resetAndRegenerate', 'Reset and Regenerate'),
      rejectLabel: t('fertWork.retainAndRegenerate', 'Retain and Regenerate'),
      onAccept: () => handleGenerateLabels(false),
      onReject: () => handleGenerateLabels(true),
    });
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const id = active.id as string;

    if (id.startsWith('mating:')) {
      const parts = id.split(':');
      const labelId = parts[1];
      const position = parts[2];
      const detailId = parts[3];
      const label = localLabels.find((l) => l.id === labelId);
      if (label) {
        setDraggedMating({ detailId, position, label });
      }
      return;
    }

    const label = localLabels.find((l) => l.id === id);
    if (label) {
      setActiveLabel(label);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (draggedMating) {
      setDraggedMating(null);
      if (!over || !sessionId) return;

      const targetId = over.id as string;
      if (!targetId.startsWith('dropzone:')) return;

      const parts = targetId.split(':');
      const targetLabelId = parts[1];
      const targetPosition = parts[2];

      try {
        const validation = await validateLabelMove({
          sessionId,
          sourceDetailId: draggedMating.detailId,
          targetLabelId,
          targetPosition,
        }).unwrap();

        if (!validation.isValid) {
          dispatch(
            addToast({
              severity: 'error',
              summary: t('fertWork.toasts.invalidMove', 'Invalid Move'),
              detail:
                validation.errorMessage ||
                t(
                  'fertWork.toasts.failedToMoveMating',
                  'Failed to move mating',
                ),
            }),
          );
          return;
        }

        if (validation.warningMessage) {
          dispatch(
            addToast({
              severity: 'warn',
              summary: t(
                'fertWork.toasts.mixedSiresWarning',
                'Mixed Sires Warning',
              ),
              detail: validation.warningMessage,
              life: 6000,
            }),
          );
        }

        await moveDetail({
          sessionId,
          sourceDetailId: draggedMating.detailId,
          targetLabelId,
          targetPosition,
        }).unwrap();
        dispatch(
          addToast({
            severity: 'success',
            summary: t('common.success', 'Success'),
            detail: t(
              'fertWork.toasts.matingMoved',
              'Mating moved successfully',
            ),
          }),
        );
        refetch();
      } catch (err: any) {
        dispatch(
          addToast({
            severity: 'error',
            summary: t('common.error', 'Error'),
            detail:
              err?.data ||
              t('fertWork.toasts.failedToMoveMating', 'Failed to move mating'),
          }),
        );
      }
      return;
    }

    setActiveLabel(null);

    if (!over || active.id === over.id) return;

    const oldIndex = localLabels.findIndex((l) => l.id === active.id);
    const newIndex = localLabels.findIndex((l) => l.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(localLabels, oldIndex, newIndex).map(
      (label, index) => ({
        ...label,
        displayOrder: index + 1,
      }),
    );

    setLocalLabels(reordered);

    if (!sessionId) return;

    const newOrder = reordered.map((label, index) => ({
      labelId: label.id,
      newDisplayOrder: index + 1,
    }));

    reorderLabels({ sessionId, newOrder })
      .unwrap()
      .catch((err: any) => {
        console.error('Failed to reorder labels:', err);
        dispatch(
          addToast({
            severity: 'error',
            summary: t('common.error', 'Error'),
            detail: t(
              'fertWork.toasts.failedToSaveLabelOrder',
              'Failed to save label order',
            ),
          }),
        );
      });
  };

  const handleDragCancel = () => {
    setActiveLabel(null);
    setDraggedMating(null);
  };

  const handlePrintAll = async () => {
    if (!sessionId) return;

    try {
      await generateLabelsPdf({
        sessionId,
      });
    } catch (err: any) {
      console.error('Failed to print PDF:', err);
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error', 'Error'),
          detail: t(
            'fertWork.toasts.failedToPrintLabels',
            'Failed to print labels',
          ),
        }),
      );
    }
  };

  const handlePrintSingleLabel = async (labelId: string) => {
    if (!sessionId) return;

    try {
      await generateSingleLabelPdf({
        sessionId,
        labelId,
      });
    } catch (err: any) {
      console.error('Failed to print label PDF:', err);
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error', 'Error'),
          detail: t(
            'fertWork.toasts.failedToPrintLabel',
            'Failed to print label',
          ),
        }),
      );
    }
  };

  const handleAddPlate = async () => {
    if (!sessionId) return;

    try {
      await createEmptyLabel({
        sessionId,
        afterLabelId: selectedLabelId ?? undefined,
      }).unwrap();
      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.success', 'Success'),
          detail: t('fertWork.toasts.blankPlateAdded', 'Blank plate added'),
        }),
      );
      refetch();
    } catch (err: any) {
      console.error('Failed to create blank plate:', err);
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error', 'Error'),
          detail:
            err?.data ||
            t(
              'fertWork.toasts.failedToCreateBlankPlate',
              'Failed to create blank plate',
            ),
        }),
      );
    }
  };

  const DraggableMating = ({
    labelId,
    position,
    detailId,
    children,
    dataTour,
  }: {
    labelId: string;
    position: string;
    detailId: string;
    children: React.ReactNode;
    dataTour?: string;
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef: setDragRef,
      isDragging,
    } = useDraggable({
      id: `mating:${labelId}:${position}:${detailId}`,
    });
    const { isOver, setNodeRef: setDropRef } = useDroppable({
      id: `dropzone:${labelId}:${position}`,
    });
    return (
      <div
        ref={(node) => {
          setDragRef(node);
          setDropRef(node);
        }}
        className={`relative ${isDragging ? 'opacity-40' : ''} ${isOver && !isDragging ? 'rounded-md ring-2 ring-blue-400' : ''}`}
      >
        <div
          {...listeners}
          {...attributes}
          data-tour={dataTour}
          className="absolute inset-y-0 left-0 z-10 flex w-5 cursor-grab touch-none items-center justify-center rounded-l-md bg-gray-200 transition-all hover:bg-gray-300"
        >
          <div className="flex flex-col gap-[2px]">
            <div className="flex gap-[2px]">
              <div className="h-[3px] w-[3px] rounded-full bg-gray-400" />
              <div className="h-[3px] w-[3px] rounded-full bg-gray-400" />
            </div>
            <div className="flex gap-[2px]">
              <div className="h-[3px] w-[3px] rounded-full bg-gray-400" />
              <div className="h-[3px] w-[3px] rounded-full bg-gray-400" />
            </div>
          </div>
        </div>
        <div className="pl-5">{children}</div>
      </div>
    );
  };

  const MatingDropZone = ({
    labelId,
    position,
    children,
    isEmpty,
  }: {
    labelId: string;
    position: string;
    children?: React.ReactNode;
    isEmpty: boolean;
  }) => {
    const { isOver, setNodeRef } = useDroppable({
      id: `dropzone:${labelId}:${position}`,
    });
    if (!isEmpty) return <>{children}</>;
    return (
      <div
        ref={setNodeRef}
        className={`flex min-h-[52px] items-center justify-center rounded-md border border-dashed p-3 transition-colors ${isOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50'}`}
      >
        <span className="text-xs text-gray-400">
          {isOver
            ? t('fertWork.dropHere', 'Drop here')
            : t('fertWork.emptyPosition', 'Empty {position}', { position })}
        </span>
      </div>
    );
  };

  const renderLabelContent = (
    label: Label,
    index: number,
    isOverlay = false,
  ) => {
    const displayLabelId = `LBL-${String(index + 1).padStart(3, '0')}`;
    return (
      <LabelCard
        label={label}
        displayLabelId={displayLabelId}
        stripeText={label.aspirationType}
        sessionId={sessionId || ''}
        isOverlay={isOverlay}
        isCommercial={isCommercial}
        onPrintLabel={handlePrintSingleLabel}
        renderMatingWrapper={({ labelId, position, detailId, children }) => (
          <DraggableMating
            labelId={labelId}
            position={position}
            detailId={detailId}
            dataTour={
              index === 0 && position === 'top'
                ? 'labels-mating-handle'
                : undefined
            }
          >
            {children}
          </DraggableMating>
        )}
        renderEmptyDropZone={({ labelId, position }) => (
          <MatingDropZone
            labelId={labelId}
            position={position}
            isEmpty={true}
          />
        )}
      />
    );
  };

  if (isLoading) {
    return (
      <div
        className={`generate-labels-page relative flex flex-auto flex-col ${className}`}
      >
        <div className="flex flex-col gap-4">
          <PageStatusBanner
            type="loading"
            message={t('fertWork.loadingLabels', 'Loading labels...')}
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`generate-labels-page relative flex flex-auto flex-col ${className}`}
      >
        <div className="flex flex-col gap-4">
          <PageStatusBanner
            type="error"
            message={t(
              'fertWork.failedToLoadLabels',
              'Failed to load labels. Please try again.',
            )}
          />
        </div>
      </div>
    );
  }

  if (labels.length === 0) {
    return (
      <div
        className={`generate-labels-page relative flex flex-auto flex-col ${className}`}
      >
        <div className="flex flex-col gap-4">
          <PageStatusBanner
            type="empty"
            message={t(
              'fertWork.noLabelsGenerated',
              'No labels generated yet. Generate labels from your runs to create IVF/Flush plate labels.',
            )}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`generate-labels-page relative flex flex-auto flex-col ${className}`}
    >
      <div className="flex flex-col gap-4">
        <div
          data-tour="labels-actions"
          ref={actionsRowRef}
          className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
        >
          <div className="flex flex-wrap gap-2">
            <Button
              variant="success"
              onClick={handleAddPlate}
              disabled={isCreatingLabel || isBusy}
              className="text-sm"
            >
              <i
                className={
                  isCreatingLabel ? 'pi pi-spin pi-spinner' : 'pi pi-plus'
                }
                data-icon="inline-start"
              />
              {t('fertWork.addPlate', 'Add Plate')}
            </Button>
            <Button
              variant="warning"
              onClick={handleRegenerateLabels}
              disabled={isBusy}
              className="text-sm"
            >
              <i
                className={isBusy ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'}
                data-icon="inline-start"
              />
              {t('fertWork.regenerate', 'Regenerate')}
            </Button>
            <Button
              onClick={handlePrintAll}
              disabled={isDownloadingAll || isBusy}
              className="text-sm"
            >
              <i
                className={
                  isDownloadingAll ? 'pi pi-spin pi-spinner' : 'pi pi-print'
                }
                data-icon="inline-start"
              />
              {t('fertWork.printAll', 'Print All')}
            </Button>
          </div>
          <div className="hidden items-center gap-2 text-sm text-gray-500 sm:flex">
            <i className="pi pi-info-circle" />
            <span>
              {t('fertWork.dragLabelsHint', 'Drag labels to reorder')}
            </span>
          </div>
        </div>

        {isBusy && (
          <PageStatusBanner
            type="loading"
            message={t('fertWork.generatingLabels', 'Generating labels...')}
          />
        )}

        <div
          data-tour="labels-color-key"
          className="rounded-lg border border-gray-200 bg-white p-3"
        >
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium text-gray-600">
              {t('fertWork.colorKey', 'Color Key:')}
            </span>
            {EMBRYO_CATEGORY_COLOR_KEY_ITEMS.map((item) => (
              <div key={item.key} className="flex items-center gap-1.5">
                <div
                  className={`${item.key === 'halfPlateDiffSires' ? 'h-3' : 'h-4'} w-6 rounded border ${item.colorClass}`}
                ></div>
                <span className="text-sm">{t(item.translationKey)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={isBusy ? 'pointer-events-none opacity-60' : ''}>
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext
              items={localLabels.map((l) => l.id)}
              strategy={rectSortingStrategy}
            >
              <div
                data-tour="labels-grid"
                className="grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-4"
              >
                {localLabels.map((label, index) => {
                  const prev = index > 0 ? localLabels[index - 1] : null;
                  const showRunHeader =
                    label.runNumber > 0 &&
                    (!prev || prev.runNumber !== label.runNumber);
                  return (
                    <React.Fragment key={label.id}>
                      {showRunHeader && (
                        <div className="col-span-full mt-1 flex items-center gap-3 first:mt-0">
                          <span className="text-sm font-semibold tracking-wide text-gray-700">
                            RUN-{label.runNumber}
                          </span>
                          <div className="h-px flex-1 bg-gray-300" />
                        </div>
                      )}
                      <SortableLabel
                        id={label.id}
                        dataTour={
                          index === 0 ? 'labels-card-handle' : undefined
                        }
                        isSelected={selectedLabelId === label.id}
                        onClick={() =>
                          setSelectedLabelId(
                            selectedLabelId === label.id ? null : label.id,
                          )
                        }
                      >
                        {renderLabelContent(label, index)}
                      </SortableLabel>
                    </React.Fragment>
                  );
                })}
              </div>
            </SortableContext>

            <DragOverlay>
              {activeLabel ? (
                renderLabelContent(
                  activeLabel,
                  localLabels.findIndex((l) => l.id === activeLabel.id),
                  true,
                )
              ) : draggedMating ? (
                <div className="rounded-md border border-blue-400 bg-white p-3 shadow-xl">
                  <div className="text-xs font-medium text-gray-500">
                    {draggedMating.position.toUpperCase()}
                  </div>
                  <div className="text-sm font-semibold">
                    {draggedMating.position === 'top'
                      ? `${draggedMating.label.top?.donorName} X ${draggedMating.label.top?.sireName}`
                      : `${draggedMating.label.bottom?.donorName} X ${draggedMating.label.bottom?.sireName}`}
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>

        {showFab && localLabels.length > 0 && !isBusy && (
          <div className="group fixed right-4 bottom-4 z-50 flex flex-col-reverse items-center gap-2 sm:right-6 sm:bottom-6 sm:gap-3">
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="success"
                    size="icon"
                    className="!h-12 !w-12 rounded-full shadow-lg sm:!h-14 sm:!w-14"
                    onClick={handleAddPlate}
                    disabled={isCreatingLabel}
                  >
                    <i
                      className={
                        isCreatingLabel ? 'pi pi-spin pi-spinner' : 'pi pi-plus'
                      }
                    />
                    <span className="sr-only">
                      {t('fertWork.addPlate', 'Add Plate')}
                    </span>
                  </Button>
                }
              />
              <TooltipContent side="left">
                {t('fertWork.addPlate', 'Add Plate')}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    size="icon"
                    className="!h-9 !w-9 translate-y-4 rounded-full opacity-0 shadow-lg transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100 sm:!h-10 sm:!w-10"
                    onClick={handlePrintAll}
                    disabled={isDownloadingAll}
                  >
                    <i
                      className={
                        isDownloadingAll
                          ? 'pi pi-spin pi-spinner'
                          : 'pi pi-print'
                      }
                    />
                    <span className="sr-only">
                      {t('fertWork.printAll', 'Print All')}
                    </span>
                  </Button>
                }
              />
              <TooltipContent side="left">
                {t('fertWork.printAll', 'Print All')}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="warning"
                    size="icon"
                    className="!h-9 !w-9 translate-y-4 rounded-full opacity-0 shadow-lg transition-all delay-75 duration-200 group-hover:translate-y-0 group-hover:opacity-100 sm:!h-10 sm:!w-10"
                    onClick={handleRegenerateLabels}
                    disabled={isGenerating || isReordering}
                  >
                    <i
                      className={
                        isGenerating || isReordering
                          ? 'pi pi-spin pi-spinner'
                          : 'pi pi-refresh'
                      }
                    />
                    <span className="sr-only">
                      {t('fertWork.regenerate', 'Regenerate')}
                    </span>
                  </Button>
                }
              />
              <TooltipContent side="left">
                {t('fertWork.regenerate', 'Regenerate')}
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};
